import React, { useContext, createContext } from 'react';
import firebase from "firebase/app";
import "firebase/auth";
import config from '../../config';
import { getFirebaseUser } from './authHelper';

if (!firebase.apps.length) {
  firebase.initializeApp(config.firebaseSettings || config.firebaseConfig);
}

const authContext = createContext();

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => useContext(authContext);

function useProvideAuth() {
  const [authData, setAuthData] = React.useState({ user: null, queryStatus: 'loading' });
  const user = authData.user;
  const setUser = React.useCallback((u) => {
    setAuthData(prev => ({ ...prev, user: u, queryStatus: 'resolved' }));
  }, []);
  const isResolved = authData.queryStatus === 'resolved';
  const deferredNullTimerRef = React.useRef(null);

  const signin = (email, password) => {
    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then((response) => {
        setUser(response.user);
        return response.user;
      });
  };

  const signup = (email, password) => {
    return firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then((response) => {
        setUser(response.user);
        return response.user;
      });
  };

  const signout = () => {
    return firebase.auth().signOut().then(() => setUser(null));
  };

  const sendPasswordResetEmail = (email) => {
    return firebase.auth().sendPasswordResetEmail(email).then(() => true);
  };

  const loggedIn = async () => (user ? user : await getFirebaseUser());

  React.useEffect(() => {
    const applyAuthState = (u) => {
      if (deferredNullTimerRef.current) {
        clearTimeout(deferredNullTimerRef.current);
        deferredNullTimerRef.current = null;
      }
      if (u != null) {
        setAuthData(prev => ({ ...prev, user: u, queryStatus: 'resolved' }));
        return;
      }
      setAuthData(prev => ({ ...prev, user: null }));
      // Firebase can fire with null before persistence restores the user; defer "resolved" so a later callback can set user.
      deferredNullTimerRef.current = setTimeout(() => {
        deferredNullTimerRef.current = null;
        setAuthData(prev => (prev.user === null ? { ...prev, queryStatus: 'resolved' } : prev));
      }, 800);
    };
    const unsubscribe = firebase.auth().onAuthStateChanged(applyAuthState);
    return () => {
      unsubscribe();
      if (deferredNullTimerRef.current) clearTimeout(deferredNullTimerRef.current);
    };
  }, []);

  return {
    authData,
    user,
    isResolved,
    signin,
    signup,
    signout,
    sendPasswordResetEmail,
    loggedIn,
  };
}
